import React, {
  useEffect,
  useReducer,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import api from '../api';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getError } from '../utils';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import Button from 'react-bootstrap/Button';
import Editor from '@monaco-editor/react';
import KeycapBaseLayer from '../components/customizer/shared/customizerScreen/KeycapBaseLayer';
import StaticKeycapShadows from '../components/customizer/shared/customizerScreen/StaticKeycapShadows';
import KeycapOverlay from '../components/customizer/shared/customizerScreen/KeycapOverlay';
import Table from 'react-bootstrap/Table';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { fetchFont } from '../utils/fontHelpers';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { calculateKeycapSizes, countKeycaps } from '../utils/keycapSizes';
import {
  calculateLayoutWidth,
  calculateLayoutHeight,
} from '../utils/layoutGeometry';
import { moldSlots, transferFilmQuantities } from '../utils/moldAssignment';
import {
  DUPLICATE_LAYOUT_CONFIRM,
  layoutSaveStop,
  layoutUpdateBody,
} from '../utils/layoutSave';
import {
  findDuplicateLayouts,
  findOverlappingRenderedKeycaps,
  checkLayout,
} from '../utils/layoutChecks';
import {
  getFoamPreviewRects,
  validatePackagingConsistency,
  calculateRequiredFoamBase,
  getMatchingPackagingDocuments,
  calculatePackagingLayoutSize,
  calculatePreviewViewBox,
} from '../utils/layoutPackaging';
import { Alert } from 'react-bootstrap';
import {
  buildLayoutSvgContentFromKeycapData,
  buildLayoutSvgFromKeycapData,
} from '../utils/cncLayoutSvg';
import { formatLayoutLabel, getProfilesFromLayout } from '../utils/layoutInfo';

const PackagingPreviewOverlay = ({
  foamBase,
  foamBaseDocuments,
  cncLayoutWidth,
  cncLayoutHeight,
}) => {
  const selectedFoam = foamBaseDocuments.find((foam) => foam._id === foamBase); // gets the currently selected foam base

  if (!selectedFoam) {
    return null; // renders nothing if no foam base is selected
  }

  const { foamRect, clearanceRect, extraFoamRects, missingFoamRects } =
    getFoamPreviewRects(selectedFoam, cncLayoutWidth, cncLayoutHeight); // the foam centred on the cnc layout, the 8mm clearance around the layout, and where the two differ

  return (
    <g>
      {/* foam base background */}
      <rect
        x={foamRect.x}
        y={foamRect.y}
        width={foamRect.width}
        height={foamRect.height}
        fill="#000"
        opacity="0.75"
      />

      {/* extra foam outside the required 8mm clearance */}
      {extraFoamRects.map((rect, index) => (
        <rect
          key={`extra-foam-${index}`}
          x={rect.x}
          y={rect.y}
          width={rect.width}
          height={rect.height}
          fill="#07bc0c"
          opacity="0.2"
        />
      ))}

      {/* missing foam where the 8mm clearance exceeds the foam boundary */}
      {missingFoamRects.map((rect, index) => (
        <rect
          key={`missing-foam-${index}`}
          x={rect.x}
          y={rect.y}
          width={rect.width}
          height={rect.height}
          fill="#e74c3c"
        />
      ))}

      {/* 8mm minimum clearance around the cnc layout */}
      <rect
        x={clearanceRect.x}
        y={clearanceRect.y}
        width={clearanceRect.width}
        height={clearanceRect.height}
        fill="none"
        stroke="#888"
        strokeWidth="0.4"
        strokeDasharray="4 4"
      />
    </g>
  );
}; // renders the selected foam, required clearance area and fit status for the cnc layout

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };

    case 'UPDATE_REQUEST':
      return { ...state, loadingUpdate: true };
    case 'UPDATE_SUCCESS':
      return { ...state, loadingUpdate: false };
    case 'UPDATE_FAIL':
      return { ...state, loadingUpdate: false };

    default:
      return state;
  }
};

export default function LayoutEditScreen() {
  const navigate = useNavigate();
  const params = useParams(); // safes the parameters (dynamic segments), defined in the URL, into the params variable: /product/:id
  const { id: layoutId } = params; // deconstracts the id out of the params variable and its name is set to productId

  const [{ loading, error, loadingUpdate }, dispatch] = useReducer(reducer, {
    loading: true,
    error: '',
  });

  const [fonts, setFonts] = useState({}); // store loaded fontkit font objects

  let totalVerticalSpacing = 0; // initialization of the totalVerticalSpacing variable. This variable is being used to accumulate the vertical spacing that comes with every row of keycaps and is needed to render the rows of keycaps at the correct height

  const [layoutName, setLayoutName] = useState('');
  const [tags, setTags] = useState([]);
  const [isFinished, setIsFinished] = useState(false);
  const [isActualLayout, setIsActualLayout] = useState(true);
  const [matchesRealKeyArrangement, setMatchesRealKeyArrangement] =
    useState(false);
  const [layout, setLayout] = useState([]);
  const [readOnly, setReadOnly] = useState(true);
  const [layoutWidth, setLayoutWidth] = useState(0);
  const [layoutHeight, setLayoutHeight] = useState(0);
  const [triggerKeycapShadowRerender, setTriggerKeycapShadowRerender] =
    useState(true);
  const [visibleGroup, setVisibleGroup] = useState('row');
  const [keycapFrequencies, setKeycapFrequencies] = useState([]);
  const [keycapFilter, setKeycapFilter] = useState('Uniform');
  const [size, setSize] = useState('100% / Full-size');
  const [numOfKeycaps, setNumOfKeycaps] = useState(0);
  const [alternativeNames, setAlternativeNames] = useState([]);
  const [keyboardModels, setKeyboardModels] = useState([]);
  const fontPromises = useRef({}); // in-flight fetches
  const [cardboardBoxes, setCardboardBoxes] = useState([]);
  const [cardboardBox, setCardboardBox] = useState(null);
  const [foamLidDocuments, setFoamLidDocuments] = useState([]);
  const [foamLid, setFoamLid] = useState(null);
  const [foamBaseDocuments, setFoamBaseDocuments] = useState([]);
  const [foamBase, setFoamBase] = useState(null);
  const [transferFilmDocuments, setTransferFilmDocuments] = useState([]);
  const [transferFilmId, setTransferFilmId] = useState('');
  const [transferFilmQuantitiesByProfile, setTransferFilmQuantitiesByProfile] =
    useState({});
  const [profileToMoldsMap, setProfileToMoldsMap] = useState({});
  const [allowFilmPooling, setAllowFilmPooling] = useState(false);
  const [existingLayouts, setExistingLayouts] = useState([]);
  const [duplicateLayoutMatches, setDuplicateLayoutMatches] = useState([]);
  const [overlappingKeycapProblems, setOverlappingKeycapProblems] = useState(
    []
  );
  const [layoutEditorValue, setLayoutEditorValue] = useState(''); // stores the raw JSON text from the editor
  const [layoutJsonError, setLayoutJsonError] = useState(''); // stores JSON parse errors from the editor
  const { problems: layoutValidationProblems, warnings: layoutWarnings } =
    useMemo(() => checkLayout(layout), [layout]); // the validator's problems in the parsed layout, and its non-blocking warnings once there are none; computed with the layout, so the effects below see them in the same render
  const validLayout = layoutValidationProblems.length > 0 ? null : layout; // the parsed layout, or null while the validator has problems: everything that reads the layout as rows of keycap objects reads this

  const layoutPreviewSvgRef = useRef(null); // gives access to the rendered svg so overlap checks can use the real svg transforms

  const loadFont = useCallback(
    (cacheKey, apiUrl) =>
      fetchFont(cacheKey, apiUrl, fonts, fontPromises.current, setFonts),
    [fonts]
  ); // loads and caches a font once (fetchFont in utils/fontHelpers.js, one copy in four screens until chain 10, Oct 2026): the loaded fonts from the state, the fetches in flight from the ref

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });

        const [
          layoutResponse,
          layoutsResponse,
          cardboardBoxesResponse,
          foamLidResponse,
          foamBaseResponse,
          transferFilmResponse,
        ] = await Promise.all([
          api.get(`/api/layouts/${layoutId}`),
          api.get(`/api/layouts/duplicate-check-data`),
          api.get(`/api/cardboard-boxes`),
          api.get(`/api/foam/lid`),
          api.get(`/api/foam/base`),
          api.get(`/api/transfer-film`),
        ]);

        const layoutData = layoutResponse.data;
        const layoutsData = layoutsResponse.data;
        const cardboardBoxesData = cardboardBoxesResponse.data;
        const foamLidData = foamLidResponse.data;
        const foamBaseData = foamBaseResponse.data;
        const transferFilmData = transferFilmResponse.data;

        setExistingLayouts(layoutsData.layouts || []);
        setCardboardBoxes(cardboardBoxesData.items);
        setFoamLidDocuments(foamLidData.items);
        setFoamBaseDocuments(foamBaseData.items);
        setTransferFilmDocuments(transferFilmData.items);

        setLayoutName(layoutData.layoutName);
        setTags(layoutData.tags);
        setIsFinished(layoutData.isFinished);
        setIsActualLayout(layoutData.isActualLayout);
        setMatchesRealKeyArrangement(layoutData.matchesRealKeyArrangement);
        setLayout(layoutData.layout);
        setLayoutEditorValue(JSON.stringify(layoutData.layout, null, 2)); // initialize the editor with the fetched layout JSON
        setLayoutWidth(calculateLayoutWidth(layoutData.layout));
        setLayoutHeight(calculateLayoutHeight(layoutData.layout));

        const frequencies = calculateKeycapSizes(layoutData.layout);
        setKeycapFrequencies(frequencies);

        setNumOfKeycaps(countKeycaps(frequencies)); // total number of keycaps

        setSize(layoutData.size);
        setAlternativeNames(layoutData.alternativeNames);
        setKeyboardModels(layoutData.keyboardModels);
        setCardboardBox(layoutData.cardboardBox);
        setFoamLid(layoutData.foamLid);
        setFoamBase(layoutData.foamBase);
        setTransferFilmId(layoutData.transferFilm?.id);
        setTransferFilmQuantitiesByProfile(
          layoutData.transferFilm?.quantitiesByProfile || {}
        );
        setAllowFilmPooling(layoutData.transferFilm?.allowFilmPooling);

        dispatch({ type: 'FETCH_SUCCESS' });
      } catch (err) {
        dispatch({
          type: 'FETCH_FAIL',
          payload: getError(err),
        });
      }
    };
    fetchData(); // fetches the product information based on the product id, from the backend
  }, [layoutId]);

  useEffect(() => {
    if (!validLayout || existingLayouts.length === 0) {
      // if the current layout has validation problems or the comparison data is not ready yet:
      setDuplicateLayoutMatches([]); // clear old duplicate warnings
      return; // stop here
    }

    const matches = findDuplicateLayouts(validLayout, existingLayouts, {
      layoutId,
      numOfKeycaps,
    }); // the saved layouts with the same key count and arrangement, this one skipped

    setDuplicateLayoutMatches(matches); // store all duplicate matches
  }, [validLayout, existingLayouts, layoutId, numOfKeycaps]); // checks whether the currently edited layout duplicates an existing saved layout

  useEffect(() => {
    const calculateTransferFilmQuantities = async () => {
      if (!validLayout) {
        // if the current layout is not ready yet or has validation problems:
        setTransferFilmQuantitiesByProfile({}); // clear old transfer film quantities
        return; // stop here
      }

      const profiles = getProfilesFromLayout(validLayout); // get all profiles that are used by the current layout

      if (profiles.length === 0) {
        // if the layout does not contain any profile data yet:
        setTransferFilmQuantitiesByProfile({}); // clear old transfer film quantities
        return; // stop here
      }

      const nextProfileToMoldsMap = { ...profileToMoldsMap }; // copy the existing mold cache so already fetched molds can be reused

      for (const profile of profiles) {
        // go through every profile used by the layout:
        if (nextProfileToMoldsMap[profile]) continue; // skip profiles where the molds were already fetched

        const { data: molds } = await api.get(`/api/molds/profile/${profile}`);
        nextProfileToMoldsMap[profile] = moldSlots(molds); // store only the mold fields needed for transfer film quantity calculation
      }

      setProfileToMoldsMap(nextProfileToMoldsMap); // update the mold cache with newly fetched molds

      const { quantitiesByProfile, profilesWithUnassignedKeycaps } =
        transferFilmQuantities(validLayout, profiles, nextProfileToMoldsMap); // the mold runs each profile needs, one transfer film per run

      profilesWithUnassignedKeycaps.forEach((profile) => {
        // if some keycaps could not be assigned to any mold slot:
        toast.warning(
          `Nicht alle Keycaps konnten einem Mold für ${profile} zugeordnet werden.`
        );
      });

      setTransferFilmQuantitiesByProfile(quantitiesByProfile); // store all calculated transfer film quantities
    };

    calculateTransferFilmQuantities(); // run the calculation
  }, [validLayout]); // recalculates the needed transfer film quantities whenever the layout changes

  useEffect(() => {
    if (!validLayout) {
      return; // stop layout calculations while the layout contains validation problems
    }

    setLayoutWidth(calculateLayoutWidth(validLayout));
    setLayoutHeight(calculateLayoutHeight(validLayout));

    const frequencies = calculateKeycapSizes(validLayout);

    setKeycapFrequencies(frequencies);

    setNumOfKeycaps(countKeycaps(frequencies)); // total number of keycaps
    setTriggerKeycapShadowRerender(!triggerKeycapShadowRerender); // triggers a rerender of the keycap shadows
  }, [validLayout]); // recalculate the layout sizes whenever the parsed layout changes

  const submitHandler = async (e) => {
    e.preventDefault(); // used, so the page doesn't refresh, when the submit button is clicked

    const stop = layoutSaveStop(
      {
        layoutJsonError,
        layoutValidationProblems,
        duplicateLayoutMatches,
        overlappingKeycapProblems,
        packagingError,
      },
      {
        confirmDuplicate: () => window.confirm(DUPLICATE_LAYOUT_CONFIRM), // ask the admin to confirm before saving a seemingly duplicate layout
        packagingConsistencyError: () =>
          validatePackagingConsistency(
            foamBase,
            foamLid,
            cardboardBox,
            foamBaseDocuments,
            foamLidDocuments,
            cardboardBoxes
          ), // checks whether all selected packaging components exist and use the same size
      }
    ); // the first gate that refuses the save, with the message to show, or the declined duplicate question

    if (stop) {
      if (stop.message) toast.error(stop.message);
      return; // stop here
    }

    try {
      dispatch({ type: 'UPDATE_REQUEST' });

      await api.put(
        `/api/layouts/${layoutId}`,
        layoutUpdateBody({
          layoutName,
          tags,
          isFinished,
          isActualLayout,
          matchesRealKeyArrangement,
          layout,
          size,
          numOfKeycaps,
          alternativeNames,
          keyboardModels,
          cardboardBox,
          foamLid,
          foamBase,
          transferFilmId,
          transferFilmQuantitiesByProfile,
          allowFilmPooling,
        })
      );
      dispatch({
        type: 'UPDATE_SUCCESS',
      });
      toast.success('Layout gespeichert');
      navigate('/admin/layouts');
    } catch (err) {
      toast.error(getError(err));
      dispatch({ type: 'UPDATE_FAIL' });
    }
  }; // a put request to the backend, that ultimalty updates the layouts information based on the data from the Form

  const handleChange = (value) => {
    const nextEditorValue = value || '';

    setLayoutEditorValue(nextEditorValue); // always keep the raw editor text in sync with Monaco

    let newLayout;

    try {
      newLayout = JSON.parse(nextEditorValue);
      setLayoutJsonError(''); // clear old JSON parse errors after successful parsing
    } catch (error) {
      setLayoutJsonError(error.message); // store the JSON parse error so the admin can fix it
      return; // stop here because invalid JSON cannot be validated as a layout (and don't update the layout if the json is invalid, so that during editing no errors are thrown)
    }

    setLayout(newLayout); // update the parsed layout state after successful JSON parsing
  }; // only parses and updates layout state

  const toggleReadOnly = () => {
    setReadOnly(!readOnly);
  };

  const handleDownloadLayoutSvg = () => {
    if (!validLayout) return;

    const svgString = buildLayoutSvgFromKeycapData(validLayout);

    const blob = new Blob([svgString], {
      type: 'image/svg+xml;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${formatLayoutLabel({ layoutName, size, tags, numOfKeycaps })}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (!validLayout) {
      // if the current layout is not ready yet or has validation problems (the preview is not rendered then):
      setOverlappingKeycapProblems([]); // clear old overlap warnings
      return; // stop here
    }

    const animationFrameId = requestAnimationFrame(() => {
      // wait until the browser has rendered the layout SVG before measuring footprint geometry:
      const overlaps = findOverlappingRenderedKeycaps(
        layoutPreviewSvgRef.current
      ); // check keycap overlaps using the actual rendered svg footprint geometry

      setOverlappingKeycapProblems(overlaps); // store all overlap problems
    });

    return () => cancelAnimationFrame(animationFrameId); // cancel old checks when the layout changes quickly
  }, [validLayout, layoutWidth, layoutHeight, visibleGroup]); // checks for overlapping keycaps after the layout svg was rendered

  const [layoutPreviewMode, setLayoutPreviewMode] = useState('normal'); // stores whether the preview shows the normal layout or the cnc foam view
  const [packagingAutoSelection, setPackagingAutoSelection] = useState(true); // controls whether packaging is selected automatically from the layout size
  const [packagingError, setPackagingError] = useState(null); // stores packaging selection errors that should block saving

  useEffect(() => {
    if (!packagingAutoSelection) {
      setPackagingError(null); // clears automatic packaging errors when manual selection is enabled
      return;
    }

    if (!validLayout || foamBaseDocuments.length === 0) {
      return; // skips automatic packaging selection until the layout and foam data are ready
    }

    const { width, height } = calculatePackagingLayoutSize(validLayout); // calculates the cnc layout size used for packaging selection

    const requiredFoamBase = calculateRequiredFoamBase(
      width,
      height,
      foamBaseDocuments
    ); // finds the smallest foam base that fits the cnc layout plus clearance

    if (!requiredFoamBase) {
      setPackagingError(
        'Das Layout ist zu groß. Es wurde kein passender Foam gefunden.'
      ); // stores an error if no foam base can fit the layout

      return;
    }

    setPackagingError(null); // clears the previous packaging error after finding a fitting foam base

    const packaging = getMatchingPackagingDocuments(
      requiredFoamBase,
      foamLidDocuments,
      cardboardBoxes
    ); // finds matching foam lid and cardboard box documents for the selected foam base

    if (!packaging.foamBase || !packaging.foamLid || !packaging.cardboardBox) {
      setPackagingError(
        'Für den berechneten Foam konnte kein passender Foam (lid) oder Cardboard Box gefunden werden.'
      ); // stores an error if matching packaging components are missing

      return;
    }

    setPackagingError(null); // clears the previous packaging error after finding a complete packaging set

    setFoamBase(packaging.foamBase); // selects the calculated foam base
    setFoamLid(packaging.foamLid); // selects the matching foam lid
    setCardboardBox(packaging.cardboardBox); // selects the matching cardboard box
  }, [
    validLayout,
    packagingAutoSelection,
    foamBaseDocuments,
    foamLidDocuments,
    cardboardBoxes,
  ]); // automatically selects the smallest fitting packaging set whenever the layout or packaging data changes

  const cncLayoutSvgContent = useMemo(() => {
    if (!validLayout) {
      return {
        width: 0,
        height: 0,
        innerSvg: '',
      }; // returns empty cnc svg content while the layout is missing or invalid
    }

    return buildLayoutSvgContentFromKeycapData(validLayout); // builds the cnc svg dimensions and keycap outline paths
  }, [validLayout]); // rebuilds the cnc svg content when the layout changes

  const cncLayoutWidth = cncLayoutSvgContent.width; // stores the cnc layout width from the generated svg content
  const cncLayoutHeight = cncLayoutSvgContent.height; // stores the cnc layout height from the generated svg content

  const previewViewBox = useMemo(() => {
    return calculatePreviewViewBox({
      cncWidth: cncLayoutWidth,
      cncHeight: cncLayoutHeight,
      layoutWidth,
      layoutHeight,
      foam: foamBaseDocuments.find((foam) => foam._id === foamBase), // the currently selected foam base
    });
  }, [
    cncLayoutWidth,
    cncLayoutHeight,
    layoutWidth,
    layoutHeight,
    foamBase,
    foamBaseDocuments,
  ]); // calculates the preview viewbox needed to show the cnc layout, foam and clearance area

  const layoutPreviewControlsDisabled = layoutPreviewMode !== 'normal'; // disables normal preview controls while the foam preview is active

  const CncLayoutFillLayer = ({ innerSvg }) => {
    if (!innerSvg) {
      return null; // renders nothing until cnc svg paths are available
    }

    const filledInnerSvg = innerSvg.replace(
      /fill="none"/g,
      'fill="#fff" opacity="0.25" stroke="none"'
    ); // converts the downloadable outline paths into filled preview shapes without adding stroke width

    return (
      <g
        pointerEvents="none"
        dangerouslySetInnerHTML={{ __html: filledInnerSvg }}
      />
    );
  }; // renders the cnc keycap footprints as filled shapes for the foam preview

  return (
    <Container>
      <Container>
        <title>Layout bearbeiten</title>

        <h1 className="screen-heading">Layout bearbeiten</h1>

        {loading || loadingUpdate ? (
          <div className="overlay">
            <LoadingLogo />
          </div>
        ) : error ? (
          <MessageBox className="danger-alert">{error}</MessageBox>
        ) : (
          <Form onSubmit={submitHandler}>
            <Form.Group className="mb-3" controlId="name">
              <Form.Label>Name</Form.Label>
              <Form.Control
                className="mobile-device-compatible"
                value={layoutName}
                onChange={(e) => setLayoutName(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="alternativeNames">
              <Form.Label>Alternative Names</Form.Label>
              <div>
                <Form.Control
                  type="text"
                  placeholder="Add a name and press Enter"
                  className="mb-2"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      setAlternativeNames([
                        ...alternativeNames,
                        e.target.value.trim(),
                      ]);
                      e.target.value = '';
                      e.preventDefault(); // Prevents form submission on Enter
                    }
                  }}
                />
                {alternativeNames.map((name, index) => (
                  <span
                    key={index}
                    className="mx-2"
                    style={{
                      border: '1px solid black',
                      borderRadius: '5px',
                      color: '#fff',
                      backgroundColor: '#1a1a1a',
                      fontSize: '14px',
                      padding: '2px 2px 2px 6px',
                    }}
                  >
                    {name}
                    <Button
                      className="no-css"
                      variant="none"
                      style={{ color: '#fff' }}
                      onClick={() =>
                        setAlternativeNames(
                          alternativeNames.filter((_, i) => i !== index)
                        )
                      }
                    >
                      &times;
                    </Button>
                  </span>
                ))}
              </div>
            </Form.Group>
            <Form.Group className="mb-3" controlId="keyboardModels">
              <Form.Label>Keyboard Models</Form.Label>
              <div>
                <Form.Control
                  type="text"
                  placeholder="Add a model and press Enter"
                  className="mb-2"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      setKeyboardModels([
                        ...keyboardModels,
                        e.target.value.trim(),
                      ]);
                      e.target.value = '';
                      e.preventDefault(); // Prevents form submission on Enter
                    }
                  }}
                />
                {keyboardModels.map((model, index) => (
                  <span
                    key={index}
                    className="mx-2"
                    style={{
                      border: '1px solid black',
                      borderRadius: '5px',
                      color: '#fff',
                      backgroundColor: '#1a1a1a',
                      fontSize: '14px',
                      padding: '2px 2px 2px 6px',
                    }}
                  >
                    {model}
                    <Button
                      className="no-css"
                      variant="none"
                      style={{ color: '#fff' }}
                      onClick={() =>
                        setKeyboardModels(
                          keyboardModels.filter((_, i) => i !== index)
                        )
                      }
                    >
                      &times;
                    </Button>
                  </span>
                ))}
              </div>
            </Form.Group>
            <Form.Group className="mb-3" controlId="tags">
              <Form.Label>Tags</Form.Label>
              <Form.Select
                className="mobile-device-compatible mb-2"
                onChange={(e) => {
                  const selectedTag = e.target.value;
                  if (selectedTag && !tags.includes(selectedTag)) {
                    setTags([...tags, selectedTag]); // Add selected tag to the array
                  }
                  e.target.value = ''; // Reset the dropdown
                }}
              >
                <option value="">Select a tag</option>
                <option className="option-tag" value="ISO">
                  ISO
                </option>
                <option className="option-tag" value="ANSI">
                  ANSI
                </option>
                <option className="option-tag" value="JIS">
                  JIS
                </option>
                <option className="option-tag" value="Ortholinear">
                  Ortholinear
                </option>
                <option className="option-tag" value="Ergonomic">
                  Ergonomic
                </option>
                <option className="option-tag" value="Split">
                  Split
                </option>
                <option className="option-tag" value="Split Spacebar">
                  Split Spacebar
                </option>
              </Form.Select>
              <div>
                {tags.map((tag, index) => (
                  <span
                    key={index}
                    className="mx-2"
                    style={{
                      border: '1px solid black',
                      borderRadius: '5px',
                      color: '#fff',
                      backgroundColor: '#1a1a1a',
                      fontSize: '14px',
                      padding: '2px 2px 2px 6px',
                    }}
                  >
                    {tag}
                    <Button
                      className="no-css"
                      variant="none"
                      style={{ color: '#fff' }}
                      onClick={() =>
                        setTags(tags.filter((_, i) => i !== index))
                      }
                    >
                      &times;
                    </Button>
                  </span>
                ))}
              </div>
            </Form.Group>
            <Form.Group className="mb-3" controlId="size">
              <Form.Label>Size</Form.Label>
              <Form.Select
                className="mobile-device-compatible"
                value={size}
                onChange={(e) => setSize(e.target.value)}
                required
              >
                <option value="100% / Full-size">100% / Full-size</option>
                <option value="96%">96%</option>
                <option value="80% / Tenkeyless (TKL)">
                  80% / Tenkeyless (TKL)
                </option>
                <option value="75%">75%</option>
                <option value="65%">65%</option>
                <option value="60% / Compact">60% / Compact</option>
                <option value="40%">40%</option>
                <option value="Kit">Kit</option>
                <option value="Others">Others</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3" controlId="numOfKeycaps">
              <Form.Label>Number of Keycaps</Form.Label>
              <Form.Control
                type="number"
                className="mobile-device-compatible"
                value={numOfKeycaps}
                disabled
                readOnly
              />
            </Form.Group>

            <div
              style={{
                border: packagingAutoSelection
                  ? '0.1rem solid #a0a0a0'
                  : '0.1rem solid black',
                borderRadius: '1rem',
                padding: '1.5rem',
                marginBottom: '0.5rem',
              }}
            >
              <div className="d-flex justify-content-end">
                <Button
                  size="sm"
                  variant="secondary"
                  className="ms-2"
                  onClick={() =>
                    setPackagingAutoSelection(!packagingAutoSelection)
                  }
                >
                  {packagingAutoSelection ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      className="bi bi-lock"
                      viewBox="0 0 16 16"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8 0a4 4 0 0 1 4 4v2.05a2.5 2.5 0 0 1 2 2.45v5a2.5 2.5 0 0 1-2.5 2.5h-7A2.5 2.5 0 0 1 2 13.5v-5a2.5 2.5 0 0 1 2-2.45V4a4 4 0 0 1 4-4M4.5 7A1.5 1.5 0 0 0 3 8.5v5A1.5 1.5 0 0 0 4.5 15h7a1.5 1.5 0 0 0 1.5-1.5v-5A1.5 1.5 0 0 0 11.5 7zM8 1a3 3 0 0 0-3 3v2h6V4a3 3 0 0 0-3-3"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      className="bi bi-unlock"
                      viewBox="0 0 16 16"
                    >
                      <path
                        fillRule="evenodd"
                        d="M12 0a4 4 0 0 1 4 4v2.5h-1V4a3 3 0 1 0-6 0v2h.5A2.5 2.5 0 0 1 12 8.5v5A2.5 2.5 0 0 1 9.5 16h-7A2.5 2.5 0 0 1 0 13.5v-5A2.5 2.5 0 0 1 2.5 6H8V4a4 4 0 0 1 4-4M2.5 7A1.5 1.5 0 0 0 1 8.5v5A1.5 1.5 0 0 0 2.5 15h7a1.5 1.5 0 0 0 1.5-1.5v-5A1.5 1.5 0 0 0 9.5 7z"
                      />
                    </svg>
                  )}
                </Button>
              </div>

              <div
                style={{
                  opacity: packagingAutoSelection ? 0.4 : 1,
                  pointerEvents: packagingAutoSelection ? 'none' : 'auto',
                  transition: 'opacity 0.2s ease',
                }}
              >
                <Form.Group className="mb-3" controlId="cardboardBox">
                  <Form.Label>Cardboard Box</Form.Label>
                  <Form.Select
                    className="mobile-device-compatible"
                    value={cardboardBox || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCardboardBox(value === '' ? null : value); // save null explicitly if empty
                    }}
                    disabled={packagingAutoSelection}
                  >
                    <option className="option-tag" value="">
                      Keine Box
                    </option>

                    {cardboardBoxes.map((box) => {
                      return (
                        <option key={box._id} value={box._id}>
                          {box.size}
                        </option>
                      );
                    })}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3" controlId="foamLid">
                  <Form.Label>Foam (lid)</Form.Label>
                  <Form.Select
                    className="mobile-device-compatible"
                    value={foamLid || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFoamLid(value === '' ? null : value);
                    }}
                    disabled={packagingAutoSelection}
                  >
                    <option className="option-tag" value="">
                      Kein Foam
                    </option>

                    {foamLidDocuments.map((foam) => {
                      return (
                        <option key={foam._id} value={foam._id}>
                          {foam.size} ({foam.type})
                        </option>
                      );
                    })}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3" controlId="foamBase">
                  <Form.Label>Foam (base)</Form.Label>
                  <Form.Select
                    className="mobile-device-compatible"
                    value={foamBase || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFoamBase(value === '' ? null : value);
                    }}
                    disabled={packagingAutoSelection}
                  >
                    <option className="option-tag" value="">
                      Kein Foam
                    </option>

                    {foamBaseDocuments.map((foam) => {
                      return (
                        <option key={foam._id} value={foam._id}>
                          {foam.size} ({foam.type})
                        </option>
                      );
                    })}
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3" controlId="transferFilmId">
              <Form.Label>Transfer Film</Form.Label>
              <Form.Select
                className="mobile-device-compatible"
                value={transferFilmId}
                onChange={(e) => {
                  setTransferFilmId(e.target.value);
                }}
                required
              >
                <option className="option-tag" value="">
                  Wähle einen Transfer Film
                </option>

                {transferFilmDocuments.map((transferFilm) => {
                  return (
                    <option key={transferFilm._id} value={transferFilm._id}>
                      {transferFilm.size}
                    </option>
                  );
                })}
              </Form.Select>
            </Form.Group>

            <Form.Group
              className="mb-3"
              controlId="transferFilmQuantitiesByProfile"
            >
              <Form.Label>Transfer Film quantities</Form.Label>

              <Table striped bordered hover style={{ fontSize: '1.4rem' }}>
                <thead>
                  <tr>
                    <th>Profile</th>
                    <th>Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(transferFilmQuantitiesByProfile).map(
                    ([profile, quantity]) => (
                      <tr key={profile}>
                        <td>{profile}</td>
                        <td>{quantity}</td>
                      </tr>
                    )
                  )}
                </tbody>
              </Table>
            </Form.Group>

            <Form.Group className="mb-3 d-flex" controlId="allowFilmPooling">
              <Form.Check
                type="checkbox"
                label="Transferfolie zwischen mehrfach bestellten Einheiten dieses Layouts teilen"
                checked={allowFilmPooling}
                onChange={(e) => setAllowFilmPooling(e.target.checked)}
              />
              <OverlayTrigger
                trigger="click"
                rootClose // close the popover when the user clicks outside of it
                placement="right"
                overlay={
                  <Popover id="popover-allowFilmPooling">
                    <Popover.Body>
                      <p>
                        When enabled, multiple{' '}
                        <strong>identical layouts (same design)</strong> within
                        the <strong>same order</strong> can share a single
                        transfer film instead of each layout requiring its own.
                      </p>
                      <hr />
                      <p className="mb-0">
                        <strong>After production:</strong> the admin should
                        enter how many transfer films were actually used for
                        pooled layouts. The system will then automatically
                        adjust stock by moving the difference (
                        <em>Δ between sold and actually used</em>) back into or
                        out of stock.
                      </p>
                    </Popover.Body>
                  </Popover>
                }
              >
                <Button className="info-popover">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="#707070"
                    className="bi bi-info-circle"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                    <path
                      d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469
                l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252
                1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0
                -.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0
                1 1 0 0 1 2 0z"
                    />
                  </svg>
                </Button>
              </OverlayTrigger>
            </Form.Group>

            <Form.Group className="mb-3" controlId="isFinished">
              <Form.Label className="d-flex">
                Finished
                <OverlayTrigger
                  trigger="click"
                  rootClose
                  placement="right"
                  overlay={
                    <Popover id="popover-isFinished">
                      <Popover.Body>
                        <p>
                          Marks whether this layout is{' '}
                          <strong>finished and ready for production</strong>.
                        </p>
                        <ul className="mb-2">
                          <li>
                            <strong>Yes</strong> – the layout is finalized and
                            can be used to create or edit product entries.
                          </li>
                          <li>
                            <strong>No</strong> – the layout is still a work in
                            progress or under development.
                          </li>
                        </ul>
                        <p>
                          This field is <strong>only</strong> used as a marker
                          to help track which layouts are still being worked on,
                          and also enforces that{' '}
                          <strong>only finished layouts</strong> can be linked
                          to
                          <strong> product documents</strong> in the{' '}
                          <em>Product Edit</em> screen.
                        </p>
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button className="info-popover ms-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="#707070"
                      className="bi bi-info-circle"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path
                        d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469
          l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252
          1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0
          -.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0
          1 1 0 0 1 2 0z"
                      />
                    </svg>
                  </Button>
                </OverlayTrigger>
              </Form.Label>
              <Form.Select
                className="mobile-device-compatible"
                value={isFinished}
                onChange={(e) => setIsFinished(e.target.value === 'true')}
                required
              >
                <option className="option-tag" value={true}>
                  Yes
                </option>
                <option className="option-tag" value={false}>
                  No
                </option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3" controlId="isActualLayout">
              <Form.Label className="d-flex">
                Actual Layout
                <OverlayTrigger
                  trigger="click"
                  rootClose
                  placement="right"
                  overlay={
                    <Popover id="popover-isActualLayout">
                      <Popover.Body>
                        <p>
                          Controls whether this layout is included in the{' '}
                          <strong>keycap frequency</strong> statistics across
                          all layouts (inside the LayoutListScreen).
                        </p>
                        <ul className="mb-2">
                          <li>
                            <strong>Yes</strong> – include this layout in
                            frequency calculations.
                          </li>
                          <li>
                            <strong>No</strong> – exclude it (useful for drafts,
                            prototypes, alternates, archived layouts, or
                            custom/private designs).
                          </li>
                        </ul>
                        <p>
                          <strong>Important:</strong> Layouts that are{' '}
                          <em>restricted to specific users</em> (custom designs)
                          should not be marked as{' '}
                          <strong>Actual Layouts</strong>, since they should not
                          influence the global keycap frequency statistics.
                        </p>
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button className="info-popover ms-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="#707070"
                      className="bi bi-info-circle"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path
                        d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469
          l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252
          1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0
          -.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0
          1 1 0 0 1 2 0z"
                      />
                    </svg>
                  </Button>
                </OverlayTrigger>
              </Form.Label>
              <Form.Select
                className="mobile-device-compatible"
                value={isActualLayout}
                onChange={(e) => setIsActualLayout(e.target.value === 'true')}
                required
              >
                <option className="option-tag" value={true}>
                  Yes
                </option>
                <option className="option-tag" value={false}>
                  No
                </option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3" controlId="matchesRealKeyArrangement">
              <Form.Label className="d-flex">
                Matches Real Key Arrangement
                <OverlayTrigger
                  trigger="click"
                  rootClose
                  placement="right"
                  overlay={
                    <Popover id="popover-matchesRealKeyArrangement">
                      <Popover.Body>
                        <p>
                          Indicates whether the <strong>key positions</strong>{' '}
                          of this layout match the real keyboard’s physical
                          arrangement <strong>100%</strong>.
                        </p>
                        <ul className="mb-2">
                          <li>
                            <strong>Yes</strong> – this layout is a 1:1 match to
                            the real keyboard layout.
                          </li>
                          <li>
                            <strong>No</strong> – it’s a
                            draft/approximation/alternative and does not fully
                            match.
                          </li>
                        </ul>
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button className="info-popover ms-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="#707070"
                      className="bi bi-info-circle"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path
                        d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469
l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252
1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0
-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0
1 1 0 0 1 2 0z"
                      />
                    </svg>
                  </Button>
                </OverlayTrigger>
              </Form.Label>

              <Form.Select
                className="mobile-device-compatible"
                value={matchesRealKeyArrangement}
                onChange={(e) =>
                  setMatchesRealKeyArrangement(e.target.value === 'true')
                }
                required
              >
                <option className="option-tag" value={true}>
                  Yes
                </option>
                <option className="option-tag" value={false}>
                  No
                </option>
              </Form.Select>
            </Form.Group>

            <Form.Group
              className="mb-3 d-flex justify-content-between align-center"
              controlId="layout"
            >
              <Form.Label>Layout</Form.Label>
              <Button
                style={{ fontSize: '12px' }}
                className="no-css"
                variant="none"
                onClick={toggleReadOnly}
              >
                {readOnly ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    fill="currentColor"
                    className="bi bi-pencil-square"
                    viewBox="0 0 16 16"
                  >
                    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                    <path
                      fillRule="evenodd"
                      d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    fill="red"
                    className="bi bi-x-square"
                    viewBox="0 0 16 16"
                  >
                    <path d="M14 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1zM2 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2z" />
                    <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708" />
                  </svg>
                )}
              </Button>
            </Form.Group>

            {packagingError && <Alert variant="danger">{packagingError}</Alert>}

            {overlappingKeycapProblems.length > 0 && (
              <Alert variant="danger">
                <div style={{ fontWeight: 'bold' }}>
                  Dieses Layout enthält überlappende Keycaps
                </div>

                <ul style={{ margin: '0.5rem 0 0 1.25rem', textAlign: 'left' }}>
                  {overlappingKeycapProblems.map((problem) => (
                    <li
                      key={`${problem.first.rowIndex}-${problem.first.keycapIndex}-${problem.second.rowIndex}-${problem.second.keycapIndex}`}
                    >
                      <span style={{ textDecoration: 'underline' }}>
                        {problem.first.label}
                      </span>{' '}
                      überschneidet sich mit{' '}
                      <span style={{ textDecoration: 'underline' }}>
                        {problem.second.label}
                      </span>
                    </li>
                  ))}
                </ul>
              </Alert>
            )}

            {layoutJsonError && (
              <Alert variant="danger">
                <div style={{ fontWeight: 'bold' }}>
                  Das Layout-JSON ist ungültig
                </div>

                <div style={{ marginTop: '0.5rem', textAlign: 'left' }}>
                  {layoutJsonError}
                </div>
              </Alert>
            )}

            {layoutValidationProblems.length > 0 && (
              <Alert variant="danger">
                <div style={{ fontWeight: 'bold' }}>
                  Dieses Layout enthält ungültige Werte oder Strukturen
                </div>

                <ul style={{ margin: '0.5rem 0 0 1.25rem', textAlign: 'left' }}>
                  {layoutValidationProblems.map((problem, index) => (
                    <li key={`${problem.path}-${index}`}>
                      <span style={{ textDecoration: 'underline' }}>
                        {problem.path}
                      </span>
                      : {problem.message}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}

            {duplicateLayoutMatches.length > 0 && (
              <Alert variant="warning">
                Dieses Layout scheint ein Duplikat zu sein:{' '}
                {duplicateLayoutMatches.map((matchedLayout, index) => (
                  <React.Fragment key={matchedLayout._id}>
                    {index > 0 && ', '}
                    <a
                      href={`/admin/layout/${matchedLayout._id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'underline', color: 'white' }}
                    >
                      {matchedLayout.layoutName}
                    </a>
                  </React.Fragment>
                ))}
              </Alert>
            )}

            {layoutWarnings.length > 0 && (
              <Alert variant="warning">
                <div style={{ fontWeight: 'bold' }}>Layout warnings</div>

                <ul style={{ margin: '0.5rem 0 0 1.25rem', textAlign: 'left' }}>
                  {layoutWarnings.map((warning, index) => (
                    <div key={index} style={{ whiteSpace: 'pre-line' }}>
                      {warning}
                    </div>
                  ))}
                </ul>
              </Alert>
            )}

            <div className="editor-container">
              <div className="ide-wrapper">
                <Editor
                  height="100%"
                  defaultLanguage="json"
                  value={layoutEditorValue}
                  onChange={handleChange}
                  options={{
                    readOnly: readOnly,
                    minimap: { enabled: true },
                    lineNumbers: 'on',
                    automaticLayout: true,
                    fontSize: 14,
                    wordWrap: 'on',
                    scrollBeyondLastLine: false,
                    quickSuggestions: true,
                    tabSize: 2,
                    bracketPairColorization: true,
                    renderWhitespace: false,
                    mouseWheelZoom: true,
                    smoothScrolling: true,
                    cursorBlinking: 'smooth',
                    cursorStyle: 'line',
                  }}
                />
              </div>

              <div className="d-flex justify-content-end my-4">
                <Button
                  variant={layoutPreviewMode === 'normal' ? 'dark' : 'light'}
                  onClick={() =>
                    setLayoutPreviewMode(
                      layoutPreviewMode === 'normal' ? 'foam' : 'normal'
                    )
                  }
                >
                  {layoutPreviewMode === 'normal'
                    ? 'Foam preview'
                    : 'Layout preview'}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-eye"
                    viewBox="0 0 16 16"
                    style={{
                      marginLeft: '0.5rem',
                    }}
                  >
                    <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8M1.173 8a13 13 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5s3.879 1.168 5.168 2.457A13 13 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5s-3.879-1.168-5.168-2.457A13 13 0 0 1 1.172 8z" />
                    <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5M4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0" />
                  </svg>
                </Button>
              </div>

              <Row className="mt-4">
                <Col md={10}>
                  <div
                    className="d-flex justify-content-center"
                    style={{
                      position: 'relative',
                    }}
                  >
                    <svg
                      ref={layoutPreviewSvgRef}
                      xmlns="http://www.w3.org/2000/svg" // Ensure correct namespace
                      viewBox={
                        layoutPreviewMode === 'normal'
                          ? `0 0 ${layoutWidth} ${layoutHeight}`
                          : `${previewViewBox.x}
                      ${previewViewBox.y}
                      ${previewViewBox.width}
                      ${previewViewBox.height}`
                      }
                      style={{
                        maxWidth:
                          layoutPreviewMode === 'normal'
                            ? layoutWidth * 3
                            : previewViewBox.width * 3,
                        width: '100%',
                        height: 'auto',
                        overflow: 'visible', // To prevent clipping (is needed to ensure that the svg is not being clipped at the bottom and right side due to rendering nuances like rounding errors and anti-aliasing)
                      }}
                      version="1.1"
                      id="customDesignedKeycapSet"
                    >
                      {/* validLayout is null while the validator has problems: the preview components read the layout as rows of keycap objects */}
                      {layoutPreviewMode === 'normal' && validLayout && (
                        <>
                          {
                            <KeycapBaseLayer
                              keycapData={validLayout}
                              totalVerticalSpacing={totalVerticalSpacing}
                              dataComingFromLayoutEditor={true}
                              dataComingFromColorPicker={false}
                              visibleGroup={visibleGroup}
                              hexColorCode={null}
                              dataComingFromLegendsEditor={false}
                            />
                          }

                          {
                            (totalVerticalSpacing = 0) /* Reset totalVerticalSpacing to 0, becaus it will be reused in the next layout.map function */
                          }

                          {
                            <KeycapOverlay
                              keycapData={validLayout}
                              settings={null} //
                              selectedTextIndex={null} //
                              selectedIconIndex={null} //
                              totalVerticalSpacing={totalVerticalSpacing}
                              handleKeycapClick={null}
                              dataComingFromLayoutEditor={true}
                              dataComingFromLegendsEditor={false}
                              fonts={fonts}
                              setFonts={setFonts}
                              loadFont={loadFont}
                            />
                          }

                          {
                            <StaticKeycapShadows
                              keycapData={validLayout}
                              triggerKeycapShadowRerender={
                                triggerKeycapShadowRerender
                              }
                              handleKeycapClick={null}
                              dataComingFromLayoutEditor={true}
                              dataComingFromLegendsEditor={false}
                            />
                          }
                        </>
                      )}

                      {layoutPreviewMode === 'foam' && (
                        <>
                          <PackagingPreviewOverlay
                            foamBase={foamBase}
                            foamBaseDocuments={foamBaseDocuments}
                            cncLayoutWidth={cncLayoutWidth}
                            cncLayoutHeight={cncLayoutHeight}
                          />

                          <CncLayoutFillLayer
                            innerSvg={cncLayoutSvgContent.innerSvg}
                          />
                        </>
                      )}
                    </svg>
                  </div>
                </Col>

                <Col
                  className="d-flex align-items-center"
                  style={{ fontSize: '12px' }}
                  md={2}
                >
                  <Table striped bordered hover style={{ marginBottom: '0px' }}>
                    <thead>
                      <tr>
                        <th>Keycap</th>
                        <th>Frequency</th>
                      </tr>
                    </thead>
                    <tbody>
                      {keycapFilter === 'Uniform'
                        ? keycapFrequencies.keycapSizes.map(
                            ([size, frequency]) => (
                              <tr key={size}>
                                <td>{size}</td>
                                <td>{frequency}</td>
                              </tr>
                            )
                          )
                        : keycapFrequencies.keycapSizesWithRow.map(
                            ([size, frequency]) => (
                              <tr key={size}>
                                <td>{size}</td>
                                <td>{frequency}</td>
                              </tr>
                            )
                          )}
                    </tbody>
                  </Table>
                </Col>
              </Row>

              <Row
                className="mt-1"
                style={{
                  opacity: layoutPreviewControlsDisabled ? 0.4 : 1,
                  pointerEvents: layoutPreviewControlsDisabled
                    ? 'none'
                    : 'auto',
                  transition: 'opacity 0.2s ease',
                }}
              >
                <Col md={10}>
                  <div className="d-flex justify-content-end mt-2">
                    <Button
                      className="mx-2"
                      onClick={() => setVisibleGroup('row')}
                      variant={visibleGroup === 'row' ? 'dark' : 'light'}
                      disabled={layoutPreviewControlsDisabled}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="22"
                        height="22"
                        fill="currentColor"
                        className="bi bi-list-ol"
                        viewBox="0 0 16 16"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5 11.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5"
                        />
                        <path d="M1.713 11.865v-.474H2c.217 0 .363-.137.363-.317 0-.185-.158-.31-.361-.31-.223 0-.367.152-.373.31h-.59c.016-.467.373-.787.986-.787.588-.002.954.291.957.703a.595.595 0 0 1-.492.594v.033a.615.615 0 0 1 .569.631c.003.533-.502.8-1.051.8-.656 0-1-.37-1.008-.794h.582c.008.178.186.306.422.309.254 0 .424-.145.422-.35-.002-.195-.155-.348-.414-.348h-.3zm-.004-4.699h-.604v-.035c0-.408.295-.844.958-.844.583 0 .96.326.96.756 0 .389-.257.617-.476.848l-.537.572v.03h1.054V9H1.143v-.395l.957-.99c.138-.142.293-.304.293-.508 0-.18-.147-.32-.342-.32a.33.33 0 0 0-.342.338zM2.564 5h-.635V2.924h-.031l-.598.42v-.567l.629-.443h.635z" />
                      </svg>
                    </Button>
                    <Button
                      className="mx-2"
                      onClick={() => setVisibleGroup('colorGroup')}
                      variant={visibleGroup === 'colorGroup' ? 'dark' : 'light'}
                      disabled={layoutPreviewControlsDisabled}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        className="bi bi-palette2"
                        viewBox="0 0 16 16"
                      >
                        <path d="M0 .5A.5.5 0 0 1 .5 0h5a.5.5 0 0 1 .5.5v5.277l4.147-4.131a.5.5 0 0 1 .707 0l3.535 3.536a.5.5 0 0 1 0 .708L10.261 10H15.5a.5.5 0 0 1 .5.5v5a.5.5 0 0 1-.5.5H3a3 3 0 0 1-2.121-.879A3 3 0 0 1 0 13.044m6-.21 7.328-7.3-2.829-2.828L6 7.188zM4.5 13a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0M15 15v-4H9.258l-4.015 4zM0 .5v12.495zm0 12.495V13z" />
                      </svg>
                    </Button>
                    <Button
                      className="mx-2"
                      onClick={() => setVisibleGroup('group')}
                      variant={visibleGroup === 'group' ? 'dark' : 'light'}
                      disabled={layoutPreviewControlsDisabled}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        fill="currentColor"
                        className="bi bi-collection"
                        viewBox="0 0 16 16"
                      >
                        <path d="M2.5 3.5a.5.5 0 0 1 0-1h11a.5.5 0 0 1 0 1zm2-2a.5.5 0 0 1 0-1h7a.5.5 0 0 1 0 1zM0 13a1.5 1.5 0 0 0 1.5 1.5h13A1.5 1.5 0 0 0 16 13V6a1.5 1.5 0 0 0-1.5-1.5h-13A1.5 1.5 0 0 0 0 6zm1.5.5A.5.5 0 0 1 1 13V6a.5.5 0 0 1 .5-.5h13a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-.5.5z" />
                      </svg>
                    </Button>

                    <div
                      style={{
                        marginLeft: '8px',
                        paddingLeft: '8px',
                        borderLeft: '1px solid #bbb',
                      }}
                    >
                      <Button
                        className="mx-2"
                        onClick={handleDownloadLayoutSvg}
                        variant={'light'}
                        disabled={layoutPreviewControlsDisabled || !validLayout}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          fill="currentColor"
                          className="bi bi-filetype-svg"
                          viewBox="0 0 16 16"
                        >
                          <path
                            fillRule="evenodd"
                            d="M14 4.5V14a2 2 0 0 1-2 2v-1a1 1 0 0 0 1-1V4.5h-2A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v9H2V2a2 2 0 0 1 2-2h5.5zM0 14.841a1.13 1.13 0 0 0 .401.823q.194.162.478.252.285.091.665.091.507 0 .858-.158.355-.158.54-.44a1.17 1.17 0 0 0 .187-.656q0-.336-.135-.56a1 1 0 0 0-.375-.357 2 2 0 0 0-.565-.21l-.621-.144a1 1 0 0 1-.405-.176.37.37 0 0 1-.143-.299q0-.234.184-.384.187-.152.513-.152.214 0 .37.068a.6.6 0 0 1 .245.181.56.56 0 0 1 .12.258h.75a1.1 1.1 0 0 0-.199-.566 1.2 1.2 0 0 0-.5-.41 1.8 1.8 0 0 0-.78-.152q-.44 0-.776.15-.337.149-.528.421-.19.273-.19.639 0 .302.123.524t.351.367q.229.143.54.213l.618.144q.31.073.462.193a.39.39 0 0 1 .153.326.5.5 0 0 1-.085.29.56.56 0 0 1-.256.193q-.167.07-.413.07-.176 0-.32-.04a.8.8 0 0 1-.248-.115.58.58 0 0 1-.255-.384zm4.575 1.09h.952l1.327-3.999h-.879l-.887 3.138H5.05l-.897-3.138h-.917zm5.483-3.293q.114.228.14.492h-.776a.8.8 0 0 0-.096-.249.7.7 0 0 0-.17-.19.7.7 0 0 0-.237-.126 1 1 0 0 0-.3-.044q-.427 0-.664.302-.235.3-.235.85v.497q0 .352.097.616a.9.9 0 0 0 .305.413.87.87 0 0 0 .518.146 1 1 0 0 0 .457-.097.67.67 0 0 0 .273-.263q.09-.164.09-.364v-.254h-.823v-.59h1.576v.798q0 .29-.096.55a1.3 1.3 0 0 1-.293.457 1.4 1.4 0 0 1-.495.314q-.296.111-.698.111a2 2 0 0 1-.752-.132 1.45 1.45 0 0 1-.534-.377 1.6 1.6 0 0 1-.319-.58 2.5 2.5 0 0 1-.105-.745v-.507q0-.54.199-.949.202-.406.583-.633.383-.228.926-.228.357 0 .635.1.282.1.48.275.2.176.314.407"
                          />
                        </svg>
                      </Button>
                    </div>
                  </div>
                </Col>
                <Col md={2}>
                  <div className="d-flex justify-content-end">
                    <Button
                      className="mx-2"
                      onClick={() => setKeycapFilter('Uniform')}
                      variant={keycapFilter === 'Uniform' ? 'dark' : 'light'}
                      disabled={layoutPreviewControlsDisabled}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="22"
                        height="22"
                        fill="currentColor"
                        className="bi bi-list"
                        viewBox="0 0 16 16"
                      >
                        <path
                          fillRule="evenodd"
                          d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5"
                        />
                      </svg>
                    </Button>
                    <Button
                      className="mx-2"
                      onClick={() => setKeycapFilter('Row')}
                      variant={keycapFilter === 'Row' ? 'dark' : 'light'}
                      disabled={layoutPreviewControlsDisabled}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="22"
                        height="22"
                        fill="currentColor"
                        className="bi bi-list-ol"
                        viewBox="0 0 16 16"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5 11.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5"
                        />
                        <path d="M1.713 11.865v-.474H2c.217 0 .363-.137.363-.317 0-.185-.158-.31-.361-.31-.223 0-.367.152-.373.31h-.59c.016-.467.373-.787.986-.787.588-.002.954.291.957.703a.595.595 0 0 1-.492.594v.033a.615.615 0 0 1 .569.631c.003.533-.502.8-1.051.8-.656 0-1-.37-1.008-.794h.582c.008.178.186.306.422.309.254 0 .424-.145.422-.35-.002-.195-.155-.348-.414-.348h-.3zm-.004-4.699h-.604v-.035c0-.408.295-.844.958-.844.583 0 .96.326.96.756 0 .389-.257.617-.476.848l-.537.572v.03h1.054V9H1.143v-.395l.957-.99c.138-.142.293-.304.293-.508 0-.18-.147-.32-.342-.32a.33.33 0 0 0-.342.338zM2.564 5h-.635V2.924h-.031l-.598.42v-.567l.629-.443h.635z" />
                      </svg>
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>

            <div className="d-flex justify-content-center align-items-center mt-5">
              <Button
                variant="secondary"
                className="mx-3"
                disabled={
                  loadingUpdate ||
                  !!layoutJsonError ||
                  layoutValidationProblems.length > 0 ||
                  overlappingKeycapProblems.length > 0 ||
                  !!packagingError // the fourth gate of the save (layoutSaveStop); without it the button looked enabled while the save refused with the packaging error's toast (fixed on the spot in chain 10, Oct 2026: user decision)
                }
                type="submit"
              >
                {/* disabled prop is set to loadingUpdate, because it should not be possible, to click the update button multiple times */}
                Speichern
              </Button>
            </div>
          </Form>
        )}
      </Container>
    </Container>
  );
}
